import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePlanPermissions, getCurrentPlan } from '../utils/planPermissions';
import { useApiErrorHandler } from './useApiErrorHandler';
import { apiFetch } from '../utils/api';

const apiBase = process.env.REACT_APP_API_URL || '/api';

export const useCustomTabs = () => {
    const { getAuthHeaders, user, activeTenantId } = useAuth();
    const [customTabs, setCustomTabs] = useState([]);
    const [loading, setLoading] = useState(false);
    const { handleApiError } = useApiErrorHandler();

    // Permissions basées sur le plan
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canUseCustomTabs } = usePlanPermissions(currentPlan);

    // Charger les onglets personnalisés
    // Dépend de `currentPlan` (string primitive) et non de `user` (objet) pour éviter
    // les re-créations en cascade à chaque re-render du contexte d'auth.
    const fetchCustomTabs = useCallback(async (signal) => {
        if (!activeTenantId) return;

        if (!canUseCustomTabs && !['team', 'business', 'enterprise'].includes(currentPlan)) {
            setCustomTabs([]);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${apiBase}/messages/tabs`, {
                headers: getAuthHeaders(),
                ...(signal && { signal })
            });

            if (!response.ok) throw new Error('Erreur lors du chargement des onglets');

            const tabs = await response.json();
            setCustomTabs(tabs);
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error('Erreur lors du chargement des onglets personnalisés:', error);
            setCustomTabs([]);
        } finally {
            setLoading(false);
        }
    }, [canUseCustomTabs, currentPlan, activeTenantId, getAuthHeaders]);

    // Créer un nouvel onglet
    const createCustomTab = useCallback(async (tabData, colorParam) => {
        if (!canUseCustomTabs) return null;

        // Support des deux formats : objet ou paramètres séparés
        const name = typeof tabData === 'string' ? tabData : tabData?.name;
        const color = typeof tabData === 'string' ? colorParam || '#6366f1' : tabData?.color || '#6366f1';

        if (!name || typeof name !== 'string' || !name.trim()) {
            throw new Error('Le nom de l\'onglet est requis et doit être une chaîne de caractères');
        }
        
        if (name.trim().length > 50) {
            throw new Error(`Le nom de l'onglet est trop long (${name.trim().length} caractères). Maximum autorisé : 50 caractères.`);
        }

        try {
            const response = await fetch(`${apiBase}/messages/tabs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ name: name.trim(), color })
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const errorMessage = errorData?.message || 'Erreur lors de la création de l\'onglet';
                throw new Error(errorMessage);
            }
            
            const newTab = await response.json();

            // Utilise le setter fonctionnel pour avoir la valeur courante (évite stale closure sur customTabs)
            setCustomTabs(prev => {
                const newTabs = [...prev, newTab];
                window.dispatchEvent(new CustomEvent('customTabsUpdated', { detail: { tabs: newTabs } }));
                return newTabs;
            });

            return newTab;
        } catch (error) {
            console.error('Erreur lors de la création de l\'onglet:', error);
            throw error;
        }
    }, [canUseCustomTabs, getAuthHeaders]);

    // Modifier un onglet
    const updateCustomTab = useCallback(async (tabId, updates) => {
        if (!canUseCustomTabs) return null;

        // Validation côté client pour les updates
        if (updates.name !== undefined) {
            if (!updates.name || !updates.name.trim()) {
                throw new Error('Le nom de l\'onglet ne peut pas être vide');
            }
            if (updates.name.trim().length > 50) {
                throw new Error(`Le nom de l'onglet est trop long (${updates.name.trim().length} caractères). Maximum autorisé : 50 caractères.`);
            }
        }

        try {
            const response = await fetch(`${apiBase}/messages/tabs/${tabId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify(updates)
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const errorMessage = errorData?.message || 'Erreur lors de la modification de l\'onglet';
                throw new Error(errorMessage);
            }
            
            const updatedTab = await response.json();

            setCustomTabs(prev => {
                const newTabs = prev.map(tab => tab._id === tabId ? updatedTab : tab);
                
                // Émettre un événement pour notifier les autres composants
                window.dispatchEvent(new CustomEvent('customTabsUpdated', { 
                    detail: { tabs: newTabs } 
                }));
                
                return newTabs;
            });
            return updatedTab;
        } catch (error) {
            console.error('Erreur lors de la modification de l\'onglet:', error);
            throw error;
        }
    }, [canUseCustomTabs, getAuthHeaders]);

    // Supprimer un onglet
    const deleteCustomTab = useCallback(async (tabId) => {
        if (!canUseCustomTabs) return false;

        try {
            const response = await fetch(`${apiBase}/messages/tabs/${tabId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            
            if (!response.ok) {
                throw new Error('Erreur lors de la suppression de l\'onglet');
            };

            // Utiliser setCustomTabs avec une fonction pour obtenir l'état le plus récent
            setCustomTabs(prevTabs => {
                const deletedTab = prevTabs.find(tab => tab._id === tabId);
                const newTabs = prevTabs.filter(tab => tab._id !== tabId);
                
                // Émettre un événement pour notifier les autres composants
                window.dispatchEvent(new CustomEvent('customTabsUpdated', { 
                    detail: { tabs: newTabs, deletedTab } 
                }));
                
                return newTabs;
            });
            
            return true;
        } catch (error) {
            console.error('Erreur lors de la suppression de l\'onglet:', error);
            throw error;
        }
    }, [canUseCustomTabs, getAuthHeaders]);

    // Ajouter des messages à un onglet
    const addMessagesToTab = useCallback(async (tabId, messageIds) => {
        if (!canUseCustomTabs) return null;

        try {
            const response = await fetch(`${apiBase}/messages/tabs/${tabId}/messages`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ messageIds, action: 'add' })
            });
            
            if (!response.ok) {
                throw new Error('Erreur lors de l\'ajout des messages à l\'onglet');
            }
            
            const updatedTab = await response.json();

            setCustomTabs(prev => {
                const newTabs = prev.map(tab => 
                    tab._id === tabId ? updatedTab : tab
                );
                
                // Émettre un événement pour notifier les autres composants
                window.dispatchEvent(new CustomEvent('customTabsUpdated', { 
                    detail: { tabs: newTabs } 
                }));
                
                return newTabs;
            });
            return updatedTab;
        } catch (error) {
            console.error('Erreur lors de l\'ajout de messages à l\'onglet:', error);
            throw error;
        }
    }, [canUseCustomTabs, getAuthHeaders]);

    // Enlever des messages d'un onglet
    const removeMessagesFromTab = useCallback(async (tabId, messageIds) => {
        if (!canUseCustomTabs) return null;

        try {
            const updatedTab = await apiFetch(`${apiBase}/messages/tabs/${tabId}/messages`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ messageIds, action: 'remove' })
            }, handleApiError);

            setCustomTabs(prev => {
                const newTabs = prev.map(tab => 
                    tab._id === tabId ? updatedTab : tab
                );
                
                // Émettre un événement pour notifier les autres composants
                window.dispatchEvent(new CustomEvent('customTabsUpdated', { 
                    detail: { tabs: newTabs } 
                }));
                
                return newTabs;
            });
            return updatedTab;
        } catch (error) {
            console.error('Erreur lors de la suppression de messages de l\'onglet:', error);
            throw error;
        }
    }, [canUseCustomTabs, getAuthHeaders, handleApiError]);

    // Masquer un onglet pour l'utilisateur actuel
    const hideTabForUser = useCallback(async (tabId) => {
        if (!canUseCustomTabs) return false;

        try {
            const response = await fetch(`${apiBase}/messages/tabs/${tabId}/hide`, {
                method: 'PUT',
                headers: getAuthHeaders()
            });
            
            if (!response.ok) {
                throw new Error('Erreur lors du masquage de l\'onglet');
            };

            // Mettre à jour l'état local
            setCustomTabs(prev => prev.map(tab => 
                tab._id === tabId ? { ...tab, isVisible: false } : tab
            ));
            return true;
        } catch (error) {
            console.error('Erreur lors du masquage de l\'onglet:', error);
            throw error;
        }
    }, [canUseCustomTabs, getAuthHeaders]);

    // Afficher un onglet pour l'utilisateur actuel
    const showTabForUser = useCallback(async (tabId) => {
        if (!canUseCustomTabs) return false;

        try {
            const response = await fetch(`${apiBase}/messages/tabs/${tabId}/show`, {
                method: 'PUT',
                headers: getAuthHeaders()
            });
            
            if (!response.ok) {
                throw new Error('Erreur lors de l\'affichage de l\'onglet');
            };

            // Mettre à jour l'état local
            setCustomTabs(prev => prev.map(tab => 
                tab._id === tabId ? { ...tab, isVisible: true } : tab
            ));
            return true;
        } catch (error) {
            console.error('Erreur lors de l\'affichage de l\'onglet:', error);
            throw error;
        }
    }, [canUseCustomTabs, getAuthHeaders]);

    // Charger les onglets au montage ou quand le tenant change
    useEffect(() => {
        const controller = new AbortController();
        fetchCustomTabs(controller.signal);
        return () => controller.abort();
    }, [fetchCustomTabs]);

    // Écouter les mises à jour des onglets depuis les autres composants
    // Note: userProfileUpdated supprimé — il causait des re-fetches inutiles à chaque sauvegarde
    // de profil. Le re-chargement est déjà couvert par les deps de fetchCustomTabs (currentPlan,
    // activeTenantId) qui détectent les vrais changements affectant les onglets.
    useEffect(() => {
        const handleCustomTabsUpdate = (event) => {
            // setTimeout pour différer la mise à jour et éviter les erreurs de setState pendant le rendu
            setTimeout(() => {
                if (event.detail?.tabs) {
                    setCustomTabs(event.detail.tabs);
                } else {
                    fetchCustomTabs();
                }
            }, 0);
        };

        window.addEventListener('customTabsUpdated', handleCustomTabsUpdate);

        return () => {
            window.removeEventListener('customTabsUpdated', handleCustomTabsUpdate);
        };
    }, [fetchCustomTabs]);

    return {
        customTabs,
        loading,
        canUseCustomTabs,
        fetchCustomTabs,
        refreshCustomTabs: fetchCustomTabs, // Alias pour plus de clarté
        createCustomTab,
        updateCustomTab,
        deleteCustomTab,
        addMessagesToTab,
        removeMessagesFromTab,
        hideTabForUser,
        showTabForUser
    };
};

export default useCustomTabs;